import type { AuthToken, HandshakeAckPayload } from './protocol.js';

const DEFAULT_PRIMARY = '#5b6bcc';
const DEFAULT_FONT = 'Source Sans 3';
const DEFAULT_RADIUS = 'md';

/**
 * Synthesise a HANDSHAKE_ACK payload for standalone mode (running outside the
 * Kadanza iframe). The `authToken` is supplied by the caller — either a real
 * token from the standalone OAuth flow, or (when OAuth isn't configured) the
 * `VITE_DEV_JWT` mock fallback. Branding/tenant context come from `VITE_DEV_*`:
 *
 *   VITE_DEV_JWT (mock fallback only)
 *   VITE_DEV_PRIMARY_COLOR
 *   VITE_DEV_FONT_FAMILY
 *   VITE_DEV_BORDER_RADIUS
 *   VITE_DEV_HOST_ORIGIN
 *   VITE_DEV_LOCALE
 *   VITE_DEV_TENANT_ID
 *   VITE_DEV_TENANT_DOMAIN
 *   VITE_DEV_EXTENSION_ID
 */
export function buildMockAck(authToken?: AuthToken): HandshakeAckPayload {
  const env = import.meta.env;
  const baseUrl = env.VITE_DEV_HOST_ORIGIN ?? 'https://boyd.dev.kadanza.dev';
  const expires = Math.floor(Date.now() / 1000 + 60 * 60).toString();

  return {
    authToken: authToken ?? {
      jwt: env.VITE_DEV_JWT ?? 'dev.mock.jwt.no-verification',
      expires,
    },
    extensionDetails: {
      extensionId: env.VITE_DEV_EXTENSION_ID ?? 'grafx-vdp-extension',
      tenantId: env.VITE_DEV_TENANT_ID ?? 'boyd',
      tenantDomain: env.VITE_DEV_TENANT_DOMAIN ?? 'boyd',
      baseUrl,
      spaceId: 'dev-space',
      pageId: 'dev-page',
      locale: env.VITE_DEV_LOCALE ?? 'en-US',
    },
    designTokens: {
      primaryColor: env.VITE_DEV_PRIMARY_COLOR ?? DEFAULT_PRIMARY,
      fontFamily: env.VITE_DEV_FONT_FAMILY ?? DEFAULT_FONT,
      borderRadius: env.VITE_DEV_BORDER_RADIUS ?? DEFAULT_RADIUS,
    },
  };
}

export function isRunningStandalone(): boolean {
  try {
    return window.parent === window;
  } catch {
    return false;
  }
}
